/**
 * Insight Selector Utility
 *
 * Maps quiz answers to insight card configurations using a static symbol lookup.
 * Pure function with no side effects or API calls.
 */

import type { QuizAnswers, InsightConfig, InsightCardType } from '../quizTypes';

/**
 * Symbol lookup table for each insight type.
 * Symbols are well-known, liquid stocks that represent each category.
 */
const INSIGHT_SYMBOLS: Record<InsightCardType, Record<string, string>> = {
  top_pick: {
    growth: 'NVDA',
    income: 'JNJ',
    preservation: 'BRK-B',
    balanced: 'MSFT',
  },
  dividend: {
    default: 'KO', // Coca-Cola - universally recognized dividend aristocrat
  },
  momentum: {
    aggressive: 'SMCI',
    moderate: 'META',
    default: 'AMZN',
  },
  risk_snapshot: {
    default: 'SPY', // S&P 500 ETF as market proxy
  },
};

/**
 * Labels and rationales for each insight card type.
 */
const INSIGHT_LABELS: Record<InsightCardType, { label: string; rationale: string }> = {
  top_pick: {
    label: 'Your Top Pick',
    rationale: 'Matches your investment profile',
  },
  dividend: {
    label: 'Dividend Spotlight',
    rationale: 'Consistent income with strong track record',
  },
  momentum: {
    label: 'Momentum Leader',
    rationale: 'Strong growth characteristics',
  },
  risk_snapshot: {
    label: 'Market Overview',
    rationale: 'Track the broader market',
  },
};

/**
 * Deep link routes for each insight card type.
 */
const INSIGHT_ROUTES: Record<InsightCardType, string> = {
  top_pick: '/stock-research',
  dividend: '/stock-research',
  momentum: '/stock-research',
  risk_snapshot: '/portfolio/risk-assessment',
};

/**
 * Selects which insight cards to show based on quiz answers.
 * Returns 2-3 InsightConfig objects (never more than 3).
 *
 * @param answers - User's quiz answers
 * @returns Array of InsightConfig objects
 */
export function selectInsights(answers: QuizAnswers): InsightConfig[] {
  const configs: InsightConfig[] = [];
  const { goal, risk, needs, persona } = answers;

  // 1. Always include top_pick (based on investment goal)
  const topPickSymbol = INSIGHT_SYMBOLS.top_pick[goal ?? 'balanced'] ?? 'MSFT';
  configs.push({
    type: 'top_pick',
    symbol: topPickSymbol,
    label: INSIGHT_LABELS.top_pick.label,
    rationale: INSIGHT_LABELS.top_pick.rationale,
    deepLinkRoute: INSIGHT_ROUTES.top_pick,
  });

  // 2. Add dividend if goal=income OR needs includes dividends
  const wantsDividend = goal === 'income' || (needs ?? []).includes('dividends');

  // 3. Add momentum if goal=growth OR risk=aggressive
  const wantsMomentum = goal === 'growth' || risk === 'aggressive';

  // 4. Add risk_snapshot if needs includes portfolio_risk OR persona=manager
  const wantsRisk = (needs ?? []).includes('portfolio_risk') || persona === 'manager';

  // Prioritize based on profile (cap at 3 total)
  // Priority: dividend/momentum (depends on goal) > risk_snapshot

  if (wantsDividend && configs.length < 3) {
    configs.push({
      type: 'dividend',
      symbol: INSIGHT_SYMBOLS.dividend.default,
      label: INSIGHT_LABELS.dividend.label,
      rationale: INSIGHT_LABELS.dividend.rationale,
      deepLinkRoute: INSIGHT_ROUTES.dividend,
    });
  }

  if (wantsMomentum && configs.length < 3) {
    const momentumSymbol =
      INSIGHT_SYMBOLS.momentum[risk ?? 'moderate'] ?? INSIGHT_SYMBOLS.momentum.default;
    configs.push({
      type: 'momentum',
      symbol: momentumSymbol,
      label: INSIGHT_LABELS.momentum.label,
      rationale: INSIGHT_LABELS.momentum.rationale,
      deepLinkRoute: INSIGHT_ROUTES.momentum,
    });
  }

  if (wantsRisk && configs.length < 3) {
    configs.push({
      type: 'risk_snapshot',
      symbol: INSIGHT_SYMBOLS.risk_snapshot.default,
      label: INSIGHT_LABELS.risk_snapshot.label,
      rationale: INSIGHT_LABELS.risk_snapshot.rationale,
      deepLinkRoute: INSIGHT_ROUTES.risk_snapshot,
    });
  }

  // Ensure at least 2 cards if we only have top_pick
  // Add a secondary insight based on goal if needed
  if (configs.length === 1) {
    if (goal === 'income' || goal === 'preservation') {
      configs.push({
        type: 'dividend',
        symbol: INSIGHT_SYMBOLS.dividend.default,
        label: INSIGHT_LABELS.dividend.label,
        rationale: INSIGHT_LABELS.dividend.rationale,
        deepLinkRoute: INSIGHT_ROUTES.dividend,
      });
    } else {
      const momentumSymbol =
        INSIGHT_SYMBOLS.momentum[risk ?? 'moderate'] ?? INSIGHT_SYMBOLS.momentum.default;
      configs.push({
        type: 'momentum',
        symbol: momentumSymbol,
        label: INSIGHT_LABELS.momentum.label,
        rationale: INSIGHT_LABELS.momentum.rationale,
        deepLinkRoute: INSIGHT_ROUTES.momentum,
      });
    }
  }

  return configs;
}
